import type { DeviceType } from '@platform/contracts';
import { createScoreboardStyles } from '@core/UI/styles/ScoreBoard.styles';
import { SKYX_MODAL_CLOSE } from '@core/UI/skyx/SkyxNewModalClose.styles';
import {
  SKYX_MODAL_CHROME,
  SKYX_MODAL_FONT,
  skyxNewModalOverlayChrome,
  skyxNewModalPanelChrome,
} from '@core/UI/skyx/SkyxNewModalChrome.styles';

export type ScoreboardStyleSheet = ReturnType<typeof createScoreboardStyles>;

const SKYX_PF_INFO_CARD_BG = '#1F0A4D';
const SKYX_PF_INFO_BASE_BG = '#140033';
const SKYX_PF_INFO_MODAL_BODY_BG = `linear-gradient(180deg, ${SKYX_PF_INFO_CARD_BG} 0%, ${SKYX_PF_INFO_BASE_BG} 100%)`;
const SKYX_PF_INFO_SECTION_BG = '#1F0A4D';
const SKYX_PF_INFO_SECTION_BORDER = '1px solid rgba(123,60,255,0.3)';
const SKYX_PF_INFO_TEXT_SECONDARY = 'rgba(201,184,245,0.7)';
const SKYX_PF_INFO_CLOSE_CHIP_BG =
  'linear-gradient(180deg, #301869 0%, #1F0D47 100%)';
const SKYX_PF_INFO_CLOSE_CHIP_BORDER = '1px solid rgba(147,92,255,0.5)';
const SKYX_PF_INFO_CLOSE_CHIP_SHADOW =
  '0 2px 3px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.1)';

function skyxPfInfoPadding(deviceType?: DeviceType): string {
  if (deviceType === 'mobile') return '22px';
  if (deviceType === 'tablet') return '28px';
  return '30px';
}

/**
 * Deep-merge scoreboard-derived modal styles so the PF info modal matches Sky X branding.
 * Only used when the active game is skyx_new / sky-x.
 */
export function mergeSkyxPfInfoModalChrome(
  base: ScoreboardStyleSheet,
  deviceType?: DeviceType
): ScoreboardStyleSheet {
  const pad = skyxPfInfoPadding(deviceType);
  return {
    ...base,
    modalOverlay: {
      ...base.modalOverlay,
      ...skyxNewModalOverlayChrome(),
      // Above primary popups (Game Rules / Settings) when opened as a nested child.
      zIndex: 10200,
      overflow: 'visible',
    },
    modalContent: {
      ...base.modalContent,
      ...skyxNewModalPanelChrome({
        background: SKYX_PF_INFO_MODAL_BODY_BG,
        borderRadius: SKYX_MODAL_CHROME.borderRadius,
      }),
    },
    modalBody: {
      ...base.modalBody,
      background: 'transparent',
      overflowX: 'hidden',
      fontFamily: SKYX_MODAL_FONT,
      padding: `0 ${pad} ${pad}`,
    },
    modalHeaderContainer: {
      ...base.modalHeaderContainer,
      background: 'transparent',
      height: 'auto',
      minHeight: '48px',
      padding: `${pad} ${pad} 20px`,
      marginBottom: 0,
      borderBottom: '1px solid transparent',
    },
    modalHeaderScrollActive: {
      borderBottom: '1px solid rgba(123,60,255,0.3)',
      boxShadow: '0 8px 16px -6px rgba(0,0,0,0.4)',
    },
    modalHeader: {
      ...base.modalHeader,
      color: '#ffffff',
      fontFamily: SKYX_MODAL_FONT,
      fontSize: '20px',
      fontWeight: 700,
      letterSpacing: '-0.01em',
      textTransform: 'none',
      textAlign: 'left',
    },
    modalCloseButton: {
      ...base.modalCloseButton,
      ...SKYX_MODAL_CLOSE.button,
      background: SKYX_PF_INFO_CLOSE_CHIP_BG,
      border: SKYX_PF_INFO_CLOSE_CHIP_BORDER,
      color: '#ffffff',
      boxShadow: SKYX_PF_INFO_CLOSE_CHIP_SHADOW,
      marginRight: 0,
      minWidth: undefined,
      fontWeight: 400,
      fontFamily: SKYX_MODAL_FONT,
    },
    modalCloseButtonHover: {
      filter: 'brightness(1.15)',
    },
    modalCloseIcon: { ...SKYX_MODAL_CLOSE.icon, color: undefined },
    diagramSection: {
      ...base.diagramSection,
      display: 'block',
      background:
        'linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.015) 100%)',
      borderTop: '1px solid rgba(255,255,255,0.08)',
      borderBottom: 'none',
      alignItems: 'stretch',
      alignSelf: 'stretch',
      width: `calc(100% + ${pad} + ${pad})`,
      maxWidth: `calc(100% + ${pad} + ${pad})`,
      marginLeft: `-${pad}`,
      marginRight: `-${pad}`,
      marginTop: '24px',
      marginBottom: '24px',
      boxSizing: 'border-box',
      padding: '16px 8px',
      fontFamily: SKYX_MODAL_FONT,
    },
    highlightedParagraph: {
      ...base.highlightedParagraph,
      background: SKYX_PF_INFO_SECTION_BG,
      border: SKYX_PF_INFO_SECTION_BORDER,
      borderRadius: '16px',
      boxShadow: '0 6px 20px rgba(6,0,20,0.35)',
      color: '#f0e8ff',
      fontFamily: SKYX_MODAL_FONT,
      lineHeight: 1.6,
    },
    modalText: {
      ...base.modalText,
      color: SKYX_PF_INFO_TEXT_SECONDARY,
      fontFamily: SKYX_MODAL_FONT,
      lineHeight: 1.6,
    },
    sectionTitle: {
      ...base.sectionTitle,
      color: '#ffffff',
      fontFamily: SKYX_MODAL_FONT,
      fontWeight: 700,
      fontSize: '15px',
      letterSpacing: '0.05em',
    },
    subTitle: {
      ...base.subTitle,
      color: '#ffffff',
      fontFamily: SKYX_MODAL_FONT,
      fontSize: '14px',
      fontWeight: 600,
      margin: '16px 0 8px',
    },
    howToCheckList: {
      ...base.howToCheckList,
      color: SKYX_PF_INFO_TEXT_SECONDARY,
      paddingLeft: '0.25rem',
      lineHeight: 1.5,
      fontFamily: SKYX_MODAL_FONT,
      margin: '10px 0 0',
    },
  };
}

/** Convenience: base scoreboard styles + Sky X PF info chrome. */
export function createSkyxPfInfoModalStyles(
  ...args: Parameters<typeof createScoreboardStyles>
): ScoreboardStyleSheet {
  const [theme, deviceType, isLandscape] = args;
  const base = mergeSkyxPfInfoModalChrome(
    createScoreboardStyles(theme, deviceType, isLandscape),
    deviceType
  );

  const isHandheldLandscape =
    !!isLandscape &&
    deviceType !== 'desktop' &&
    (deviceType === 'mobile' ||
      (deviceType === 'tablet' &&
        typeof window !== 'undefined' &&
        window.innerHeight <= 520));

  if (!isHandheldLandscape) {
    return base;
  }

  return {
    ...base,
    modalOverlay: {
      ...base.modalOverlay,
      zIndex: 10200,
      paddingTop: 'calc(var(--headerHeightDynamic, 42px) + 8px)',
    },
    modalContent: {
      ...base.modalContent,
      maxHeight: 'calc(100dvh - var(--headerHeightDynamic, 42px) - 16px)',
    },
  };
}
